import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const categories = [
  {
    slug: "build",
    number: "01",
    title: "Build",
    tagline:
      "Production-grade products, designed and engineered end-to-end.",
    items: [
      { name: "Mobile Apps", desc: "iOS & Android, native-feel cross-platform builds." },
      { name: "Websites & Landing Pages", desc: "Marketing sites that convert and compound." },
      { name: "SaaS MVPs", desc: "Idea to revenue-ready product in weeks, not quarters." },
      { name: "E-commerce & Marketplaces", desc: "Storefronts, vendor flows, payments, ops." },
      { name: "Internal Tools & Dashboards", desc: "Admin panels and ops UIs your team actually uses." },
    ],
    image:
      "https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&w=1200&q=80",
  },
  {
    slug: "automate",
    number: "02",
    title: "Automate",
    tagline:
      "AI agents, chatbots and workflow automations that run your business 24/7.",
    items: [
      { name: "CRM Design & Implementation", desc: "Tailored CRMs around how your team actually sells." },
      { name: "AI Agents", desc: "Autonomous agents for support, sales and ops." },
      { name: "Chatbots", desc: "Web, WhatsApp and in-app conversational layers." },
      { name: "Workflow Automation", desc: "Connect tools, kill busywork, scale output." },
      { name: "API Integrations", desc: "Stripe, WhatsApp, Slack, HubSpot, and beyond." },
    ],
    image:
      "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=80",
  },
  {
    slug: "consult",
    number: "03",
    title: "Consult",
    tagline:
      "Strategy, architecture and analytics for leaders making big tech decisions.",
    items: [
      { name: "Tech Strategy", desc: "Roadmaps, build-vs-buy, vendor selection." },
      { name: "Product Architecture", desc: "Scalable systems designed before they're written." },
      { name: "Data Dashboards & Analytics", desc: "See your business clearly, in real time." },
      { name: "Booking & Scheduling Systems", desc: "Calendars, reminders, payments — fully owned." },
      { name: "Fractional CTO", desc: "Senior engineering leadership on demand." },
    ],
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80",
  },
];

export default function Services() {
  const [active, setActive] = useState("build");

  return (
    <section
      id="services"
      data-testid={SERVICES.root}
      className="relative py-24 md:py-36 bg-[#F5F4F0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
          <div>
            <div className="caption mb-6">Services</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#1A1A1A] leading-[1.05] max-w-3xl">
              Three disciplines. <span className="italic">One studio.</span>
            </h2>
          </div>
          <p className="max-w-md text-[#6B6A68] md:text-right">
            Whether you need a single product shipped or a whole tech function
            run for you, our work falls into three clear categories.
          </p>
        </div>

        <div className="border-t border-[#1A1A1A]/15">
          {categories.map((cat) => {
            const isOpen = active === cat.slug;
            return (
              <div
                key={cat.slug}
                data-testid={SERVICES.row(cat.slug)}
                className="service-row border-b border-[#1A1A1A]/15"
              >
                <button
                  onClick={() => setActive(isOpen ? "" : cat.slug)}
                  className="w-full flex items-center justify-between gap-8 py-8 md:py-10 text-left group"
                >
                  <div className="flex items-baseline gap-6 md:gap-10">
                    <span className="font-mono-tech text-xs md:text-sm text-[#6B6A68] pt-2">
                      {cat.number}
                    </span>
                    <span className="font-serif-display text-4xl sm:text-5xl md:text-7xl text-[#1A1A1A] tracking-tight leading-none">
                      {cat.title}
                    </span>
                  </div>
                  <span className="shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full border border-[#1A1A1A]/30 flex items-center justify-center text-[#1A1A1A] group-hover:bg-[#1A1A1A] group-hover:text-[#FDFCFB] transition-colors">
                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: [0.2, 0.6, 0.2, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="pb-12 grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-7 md:pl-[88px]">
                          <p className="font-serif-display italic text-xl md:text-2xl text-[#1A1A1A]/85 mb-8 max-w-2xl">
                            {cat.tagline}
                          </p>
                          <ul className="divide-y divide-[#1A1A1A]/10 border-y border-[#1A1A1A]/10">
                            {cat.items.map((item) => (
                              <li
                                key={item.name}
                                className="py-4 flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-6"
                              >
                                <span className="text-[#1A1A1A] font-medium tracking-tight w-full sm:w-1/3">
                                  {item.name}
                                </span>
                                <span className="text-[#6B6A68] text-sm sm:text-base flex-1">
                                  {item.desc}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="md:col-span-5 md:pl-6">
                          <div className="relative w-full aspect-[4/3] md:aspect-[3/4] overflow-hidden rounded-sm">
                            <img
                              src={cat.image}
                              alt={cat.title}
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
